import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setSearchQuery,
  setGroupingOption,
  setSortingOption,
  setYearRange,
  setMonths,
  setSelectedTags,
  togglePolygonSelection,
  toggleGroupSelection,
  selectAllPolygons,
  selectAllGroups,
  deselectAllPolygons,
  deselectAllGroups,
  fetchExplorerData,
  fetchPolygonsForGroups,
  fetchAnalyticsData,
  fetchAvailableYears,
  clearSearchAndPolygonData,
  type DataExplorerState,
  setHistoryFilters,
  fetchHistoryAttributeValues,
  resetAllFilters,
} from '../../store/dataExplorerSlice';
import SearchFilterControls from '../common/SearchFilterControls';
import { getAllTags, getBoundaries } from '../../services/apiClient';
import PolygonSelectionPanel from '../common/PolygonSelectionPanel';
import ArcGISMap from '../common/ArcGISMap';
import { GroupingOption } from '../../types/enums';
import { useAccountContext } from '../../hooks/useAccountContext';
import { HistoryFilterPopup } from '../common/HistoryFilterPopup';
import { BoundaryData } from '../../types/schema';

const MapContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const ContentGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '350px 1fr',
  gap: theme.spacing(3),
  flexGrow: 1,
  minHeight: 0, // Ensure flex child can shrink
}));

const MapWrapper = styled(Box)({
  height: '100%',
  width: '100%',
  position: 'relative',
  borderRadius: '8px',
  overflow: 'hidden',
});

const MapView: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    searchQuery,
    groupingOption,
    sortingOption,
    startYear,
    endYear,
    months,
    selectedTags,
    selectedPolygonIds,
    selectedGroupIds,
    polygons,
    polygonGroups,
    groupedPolygonDetails,
    status,
    groupDetailsStatus,
    historyFilters,
    historyAttributeValues,
    isYearRangeFinal,
    historyAttributeStatus,
  } = useSelector((state: RootState) => state.dataExplorer as DataExplorerState);
  const { selectedAccount, isAdmin } = useSelector((state: RootState) => state.app);
  const { accountId } = useAccountContext();
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [isHistoryFilterOpen, setHistoryFilterOpen] = useState(false);
  const [isLoadingPolygons, setIsLoadingPolygons] = useState(false);
  const [boundaries, setBoundaries] = useState<BoundaryData[]>([]);
  const [showBoundaries, setShowBoundaries] = useState(true);

  // Removing this to retain polygon / group selection on toggling Analytics & Map views
  // useEffect(() => {
  //   // Return a cleanup function to run when the component unmounts
  //   return () => {
  //     dispatch(clearSearchAndPolygonData());
  //   };
  // }, [dispatch]);

  // Fetch boundaries
  useEffect(() => {
    getBoundaries(isAdmin ? selectedAccount?.accountId : undefined)
        .then(setBoundaries)
        .catch(err => console.error("Failed to fetch boundaries", err));
  }, [isAdmin, selectedAccount?.accountId]);

  // Fetch available years on mount and when account changes
  useEffect(() => {
    dispatch(fetchAvailableYears()).then((result) => {
      if (fetchAvailableYears.fulfilled.match(result)) {
        const years = result.payload.years;
        if (years && years.length > 0) {
          setAvailableYears(years);
        }
      }
    });
  }, [dispatch, accountId]);

  const fetchTags = useCallback(async () => {
    try {
      const tags = await getAllTags(isAdmin ? selectedAccount?.accountId : undefined);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      setAvailableTags([]);
    }
  }, [isAdmin, selectedAccount?.accountId]);

  useEffect(() => {
    if (availableTags.length === 0) {
      fetchTags();
    }
  }, [availableTags.length, fetchTags]);

  useEffect(() => {
    if (!isYearRangeFinal) return;
    setIsLoadingPolygons(true);
    dispatch(fetchExplorerData()).finally(() => {
      setIsLoadingPolygons(false);
    });
  }, [dispatch, isYearRangeFinal, groupingOption, startYear, endYear, months, selectedAccount, searchQuery, selectedTags, historyFilters]);

  // Fetch analytics data when filters change
  useEffect(() => {
    if (!isYearRangeFinal) return;
    if (selectedPolygonIds.length > 0 || selectedGroupIds.length > 0) {
      dispatch(fetchAnalyticsData());
    }
  }, [dispatch, isYearRangeFinal, selectedPolygonIds, selectedGroupIds, startYear, endYear, months, selectedAccount, selectedTags, historyFilters]);

  // Fetch polygon details when groups are selected (for map view)
  // Also refetch when year range or months change to update NEE values
  useEffect(() => {
    if (!isYearRangeFinal) return;
    if (groupingOption !== GroupingOption.NONE && selectedGroupIds.length > 0) {
      dispatch(fetchPolygonsForGroups());
    }
  }, [dispatch, isYearRangeFinal, selectedGroupIds, groupingOption, startYear, endYear, months]);

  const handlePolygonToggle = (polygonId: number) => {
    dispatch(togglePolygonSelection(polygonId as number));
  };

  const handleGroupToggle = (groupId: string) => {
    dispatch(toggleGroupSelection(groupId));
  };

  const handleSelectAll = () => {
    if (groupingOption === GroupingOption.NONE) {
        const allPolygonIds = polygons.map(p => p.id);
        dispatch(selectAllPolygons(allPolygonIds as number[]));
    } else {
        const allGroupIds = polygonGroups.map(g => g.id);
        dispatch(selectAllGroups(allGroupIds));
    }
  };

  const handleDeselectAll = () => {
    if (groupingOption === GroupingOption.NONE) {
        dispatch(deselectAllPolygons());
    } else {
        dispatch(deselectAllGroups());
    }
  };

  const handleGroupingChange = useCallback((option: GroupingOption) => {
    dispatch(setGroupingOption(option));
  }, [dispatch]);

  const handleResetAll = () => {
    dispatch(resetAllFilters());
  };

  return (
    <MapContainer>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        Map
      </Typography>

      <SearchFilterControls
        searchQuery={searchQuery}
        onSearchChange={(query) => dispatch(setSearchQuery(query))}
        startYear={startYear}
        onStartYearChange={(year) => dispatch(setYearRange({ startYear: year, endYear }))}
        endYear={endYear}
        onEndYearChange={(year) => dispatch(setYearRange({ startYear, endYear: year }))}
        months={months}
        onMonthsChange={(newMonths) => dispatch(setMonths(newMonths))}
        availableTags={availableTags}
        selectedTags={selectedTags}
        onTagsChange={(tags) => dispatch(setSelectedTags(tags))}
        availableYears={availableYears.length > 0 ? availableYears : undefined}
        onHistoryFilterClick={() => setHistoryFilterOpen(true)}
        historyFilters={historyFilters}
        onResetAll={handleResetAll}
      />

      <ContentGrid>
        <PolygonSelectionPanel
          groupingOption={groupingOption}
          onGroupingChange={handleGroupingChange}
          sortingOption={sortingOption}
          onSortingChange={(option) => dispatch(setSortingOption(option))}
          polygons={polygons}
          polygonGroups={polygonGroups}
          selectedPolygons={selectedPolygonIds as number[]}
          selectedGroups={selectedGroupIds}
          onPolygonToggle={handlePolygonToggle}
          onGroupToggle={handleGroupToggle}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          title={groupingOption === GroupingOption.NONE ? `Polygons (${polygons.length})` : `Groups (${polygonGroups.length})`}
          loading={isLoadingPolygons}
        />
        <MapWrapper>
          <ArcGISMap
            polygons={polygons}
            selectedPolygonIds={selectedPolygonIds as number[]}
            groupingOption={groupingOption}
            polygonGroups={polygonGroups}
            selectedGroupIds={selectedGroupIds}
            groupedPolygonDetails={groupedPolygonDetails}
            groupDetailsStatus={groupDetailsStatus}
            boundaries={boundaries}
            showBoundaries={showBoundaries}
            onToggleBoundaries={() => setShowBoundaries(!showBoundaries)}
            onPolygonClick={handlePolygonToggle}
          />
        </MapWrapper>
      </ContentGrid>
      <HistoryFilterPopup
        open={isHistoryFilterOpen}
        onClose={() => setHistoryFilterOpen(false)}
        onApply={(filters) => dispatch(setHistoryFilters(filters))}
        initialFilters={historyFilters}
        attributeValues={historyAttributeValues}
        isLoading={historyAttributeStatus === 'loading'}
        onFetchValues={() => dispatch(fetchHistoryAttributeValues())}
      />
    </MapContainer>
  );
};

export default MapView;
